import { Button, type ButtonProps } from '@/components/ui/button.tsx'
import { Dialog, DialogContent, DialogDescription, DialogOverlay, DialogTitle } from '@radix-ui/react-dialog'
import { LucideChevronLeft, LucideChevronRight, LucideXCircle } from 'lucide-react'
import { type MouseEvent, useCallback, useEffect, useState } from 'react'

interface ImageViewerProps {
  images: string[]
  initialIndex: number
  onClose: () => void
}

export const AttachmentViewer = ({ images, initialIndex = -1, onClose }: ImageViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  const handleNext = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      setCurrentIndex((prev) => (prev + 1) % images.length)
    },
    [images.length],
  )

  const handlePrevious = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
    },
    [images.length],
  )

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation()
    onClose()
  }

  if (currentIndex === -1 || images.length === 0) return null

  return (
    <Dialog open={currentIndex !== -1} onOpenChange={() => onClose()}>
      <DialogOverlay className="fixed inset-0 bg-black/80 z-30" />
      <DialogContent className="fixed inset-0 p-0 w-screen h-screen z-30">
        <DialogTitle className="sr-only">
          Image Gallery - Image {currentIndex + 1} of {images.length}
        </DialogTitle>
        <DialogDescription className="sr-only" />
        <div className="relative w-full h-full flex items-center justify-center">
          <CloseButton onClose={handleClose} />
          <div
            className="w-full h-full bg-no-repeat bg-center bg-contain"
            style={{ backgroundImage: `url(${images[currentIndex]})` }}
            role="img"
            aria-label={`img ${currentIndex + 1}`}
            data-testid="current-image"
          />
          <NavigationButton direction="previous" onClick={handlePrevious} disabled={images.length === 1} />
          <NavigationButton direction="next" onClick={handleNext} disabled={images.length === 1} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

const CloseButton = ({ onClose }: { onClose: (e: MouseEvent) => void }) => (
  <Button
    size="icon"
    variant="ghost"
    className="absolute top-4 right-4 text-white hover:bg-white/10"
    aria-label="Close image viewer"
    onClick={onClose}
  >
    <LucideXCircle />
  </Button>
)

const NavigationButton = ({ direction, ...props }: { direction: 'next' | 'previous' } & ButtonProps) => (
  <Button
    size="icon"
    variant="ghost"
    className={`absolute inset-y-1/2 ${direction === 'previous' ? 'left-4' : 'right-4'} text-white hover:bg-white/10`}
    aria-label={`${direction} image`}
    {...props}
  >
    {direction === 'previous' ? <LucideChevronLeft /> : <LucideChevronRight />}
  </Button>
)
